import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DynamicTable from "components/ui/TableComponents/DynamicTable";
import { useFilter } from "context/FilterContext";
import SortContext from "context/SortContext";
import React, { useEffect } from "react";
import { useGlobalContext } from "context/GlobalContext";
import { SettingContext } from "context/SettingContext";
import { useLocation, useNavigate } from "react-router-dom";
import { useServiceRequests, useServiceRequestsInfinite } from "./API/getAllServiceRequest";
import { filter } from "lodash";
import { useInView } from "react-intersection-observer";
import { formatDate } from "utils/formatDate";
import { usePermissions } from "context/PermissionsContext";
import { findSpecificMenu, findSpecificSubMenu, isAllowed } from "utils/checkUserRigths";

const ServiceRequest = () => {
  const { setFilter, setBackendFilterValues, setFilterCount, setAppliedFilterValues, setFilterValues, filterValues } = useFilter();
  const { setSelectedSortValue, selectedSortValue } = React.useContext(SortContext);
  const { setSearchBar, search } = React.useContext(SettingContext);

  const navigate = useNavigate();
  const location = useLocation();
  const { ref, inView } = useInView();

  const { setSortOptions, setSearchPlaceHolder, sortOptions, setServiceRequestCounts } = useGlobalContext();

  const selectedOption = sortOptions.find((option) => option.id == selectedSortValue);
  const selectedSortBY = selectedOption?.sortBy;
  const selectedSortOrder = selectedOption?.order;

  const formatIndianNumber = (num) => {
    const str = String(num).trim();
  
    // already formatted
    if (str.startsWith("+91")) return str;
  
    // backend gives 91XXXXXXXXXX (12 digits)
    if (str.startsWith("91") && str.length === 12) {
      return `+91 ${str.slice(2)}`;
    }
  
    // if only 10 digit number
    if (str.length === 10) {
      return `+91 ${str}`;
    }
  
    return str;
  };
  
  const permission=usePermissions();
  const serviceRequestsSubMenu = findSpecificSubMenu(findSpecificMenu("Vision Web", permission), "Service Requests");
  const hasAccessManagementAction = isAllowed(serviceRequestsSubMenu, "management");
  const showClientName = isAllowed(serviceRequestsSubMenu, "clientName");
  const showStatusProduction = permission?.isAdmin ? false : !isAllowed(serviceRequestsSubMenu, "status");

  const handleClick = (data) => {
    let newState = {
      ...location.state,
      name: [`${location?.state?.name[0]?.split(" - ")[0]}`, `#${data?.serviceRequestId} (${data?.clientName})`],
    };
    navigate(`/Setting/service-request/${data?.serviceRequestId}`, { state: newState });
  };

  const headers = [
    { label: "Sr. No" },
    ...(showClientName ? [{ label: "Client Name" }] : []),
    { label: "Client Code" },
    { label: "Category" },
    { label: "Design No" },
    { label: "SR Number" },
    { label: "SR Date" },
    { label: "Mobile No" },
    { label: "SR Status" },
    { label: "SR Type" },
    { label: "Action" },
  ];

  //API Call

  const { data, isLoading, isFetching, fetchNextPage, hasNextPage, isFetchingNextPage, error } = useServiceRequestsInfinite({
    search,
    statusFilter: showStatusProduction ? ["Production"] : (filterValues["Status"] || []),
    serviceTypeFilter: filterValues["Service Request Type"] || [],
    sortBy: selectedSortBY || "DATE_DESC",
    limit: 10,
  });
  // console.log("data====>>>",data)

  const serviceRequests = data?.pages.flatMap((page) => page?.data || []) || [];

  // console.log("serviceRequests====>>>",serviceRequests)

  const statusStyles = {
    "Review":{
      color: "#50C878",
      background: "#50C8781A",
    },
    "Accepted": {
      color: "#FFD700",
      background: "#FFD7001A",
    },
    "Received": {
      color: "#8C50C8",
      background: "#8C50C81A",
    },
    "Production": {
      color: "#51A47A",
      background: "#51A47A1A",
    },
    "Need your Approval": {
      color: "#CC5078",
      background: "#CC50781A",
    },
    "Ready for Dispatch": {
      color: "#5078C8",
      background: "#5078C81A",
    },
    "Dispatched": {
      color: "#8B8B8B",
      background: "#8B8B8B1A",
    },
    "Cancelled": {
      color: "#E77B7B",
      background: "#E77B7B1A",
    },
  };

  const sections = [
    {
      headers,
      rows: serviceRequests.map((row, rowIndex) => {
        const statusStyle = statusStyles[row.currentStatus] || {
          color: "#50C878",
          background: "#50C8781A",
        };
        const columns = [
          rowIndex + 1,
          ...(showClientName ? [row.clientName] : []),
          row.partyCode,
          row.categoryName,
          row.designNo,
          row.serviceRequestId,
          formatDate(row.createdAt),
          formatIndianNumber(row.clientNumber ?? "-"),
          <div
            style={{
              display: "inline-block",
              padding: "4px 12px",
              borderRadius: "6px",
              color: statusStyle.color,
              fontSize: "10px",
              fontWeight: 500,
              fontFamily: "Poppins",
              background: statusStyle.background,
              whiteSpace: "nowrap",
            }}
          >
            {row.currentStatus}
          </div>,

          row.serviceType,

          // Action icon
          <div
            onClick={() => handleClick(row)}
            style={{
              width: "42px",
              height: "36px",
              border: "1px solid #fff",
              borderRadius: "6px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              margin: "auto",
            }}
          >
            <VisibilityOutlinedIcon sx={{ color: "#fff" }} />
          </div>,
        ];

        return columns.map((cell, cellIndex) => (
          <div
            key={`${rowIndex}-${cellIndex}`}
            style={{
              backgroundColor: "transparent",
              color: "#fff",
              fontWeight: 400,
              fontFamily: "Karla",
              fontSize: "16px",
              width: "100%",
              height: "100%",
              padding: "8px 0",
              textAlign: "center",
            }}
          >
            {cell}
          </div>
        ));
      }),
    },
  ];

  useEffect(() => {
    if (!data?.pages?.length) return;
    const counts = data.pages[0]?.counts;
    if (counts) {
      setServiceRequestCounts(counts);
    }
  }, [data, setServiceRequestCounts]);
  useEffect(() => {
    setSortOptions([
      { id: 1, name: "Client Name (A to Z)", sortBy: "CLIENT_NAME_ASC", order: "asc" },
      { id: 2, name: "Client Name (Z to A)", sortBy: "CLIENT_NAME_DESC", order: "desc" },

      { id: 3, name: "Client Code (Low to High)", sortBy: "CLIENT_CODE_ASC", order: "asc" },
      { id: 4, name: "Client Code (High to Low)", sortBy: "CLIENT_CODE_DESC", order: "desc" },
      { id: 5, name: "Design No. (Low to High)", sortBy: "DESIGN_NO_ASC", order: "asc" },
      { id: 6, name: "Design No. (High to Low)", sortBy: "DESIGN_NO_DESC", order: "desc" },

      { id: 7, name: "Service Request No. (Low to High)", sortBy: "SR_NO_ASC", order: "asc" },
      { id: 8, name: "Service Request No. (High to Low)", sortBy: "SR_NO_DESC", order: "desc" },
      { id: 9, name: "Service Request Date (Oldest to Newest)", sortBy: "DATE_ASC", order: "asc" },
      { id: 10, name: "Service Request Date (Newest to Oldest)", sortBy: "DATE_DESC", order: "desc" },
    ]);
    let filterData = {
      "Service Request Type": ["Manufacturing Defects", "QC Failed", "Repairing Request"],
      Status: showStatusProduction
        ? ["Production"]
        : ["Review", "Accepted", "Received", "Production", "Need your Approval", "Ready for Dispatch", "Dispatched", "Cancelled"],
    };

    setFilter(true);
    setBackendFilterValues((prev) => filterData);

    setFilterValues((prev) => {
      const mergedFilters = { ...filterData };
      for (const key in prev) {
        if (Array.isArray(prev[key]) && prev[key].length > 0) {
          if (showStatusProduction && key === "Status") continue;
          mergedFilters[key] = prev[key];
        }
      }
      return mergedFilters;
    });
    setSearchBar(true);
    setSearchPlaceHolder("Search by Client name or SR number");

    return () => {
      setSortOptions([]);
      setSelectedSortValue(null);
      setFilter(false);
      setSearchBar(false);
      setFilterCount(0);
      setFilterValues({});
      setBackendFilterValues({});
      setAppliedFilterValues({});
      setSearchPlaceHolder("");
    };
  }, [showStatusProduction]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.innerHeight + window.scrollY >= document.body.offsetHeight - 200 && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [hasNextPage, isFetchingNextPage]);

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage]);

  if (error) return <p>Error loading Service</p>;

  return (
    <div style={{ padding: "10px", background: "#000" }}>
      <DynamicTable sections={sections} loading={isLoading} error={false} maxHeight={"70vh"} isFetchingNextPage={isFetchingNextPage} loadMoreRef={ref} />
    </div>
  );
};

export default ServiceRequest;
